import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef
} from "@angular/core";
import { TuiDay, TuiMonth } from "@taiga-ui/cdk";
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { isNotEmptyValue } from "kwikid-toolkit";
import { throwErrorMessage } from "../../../../helpers/kwikui.common.helpers";
import { DEFAULT_VALUES, VALIDATE_KEY_VALUES } from "./calendar.constants";
import { isValidKeyValue } from "./calendar.validation";

@Component({
  selector: "kwikui-calendar",
  templateUrl: "./calendar.component.html",
  styleUrls: ["./calendar.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KwikUICalendarComponent),
      multi: true
    }
  ]
})
export class KwikUICalendarComponent
  implements OnInit, OnChanges, ControlValueAccessor {
  @Input() disabled: boolean = DEFAULT_VALUES.disabled;

  @Input() disableItems: Function = DEFAULT_VALUES.disableItems;

  @Input() formControl: FormControl = new FormControl({});

  @Input() formControlName: string = DEFAULT_VALUES.formControlName;

  @Input() id: string = DEFAULT_VALUES.id;

  @Input() isShowAdjacentValues: boolean = DEFAULT_VALUES.isShowAdjacentValues;

  @Input() min: TuiDay = DEFAULT_VALUES.min;

  @Input() max: TuiDay = DEFAULT_VALUES.max;

  @Input() value: TuiDay = DEFAULT_VALUES.value;

  @Output() onDayClick: EventEmitter<any> = new EventEmitter<any>();

  @Output() onMonthChange: EventEmitter<any> = new EventEmitter<any>();

  @Output() getKeyValue: EventEmitter<any> = new EventEmitter<any>();

  formGroup: FormGroup = new FormGroup({});

  constructor() {}

  ngOnInit(): void {
    if (isNotEmptyValue(this.formControl.value)) {
      this.value = this.formControl.value;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const verifyChange = (key: string) => {
      return changes.hasOwnProperty(key) && !changes[key].firstChange;
    };

    for (const change of Object.entries(changes)) {
      const key: string = change[0];
      const value: any = change[1].currentValue;
      this.validateInputProperty(key, value);
    }

    if (verifyChange("formControl")) {
      this.formControl = changes.formControl.currentValue;
      this.value = this.formControl.value;
    }

    if (verifyChange("disabled")) {
      this.disabled = Boolean(changes.disabled.currentValue);
      this.setDisabled();
    }
  }

  private validateInputProperty(key: string, value: any): void {
    if (VALIDATE_KEY_VALUES[key] && !isValidKeyValue(key, value)) {
      this[key] = DEFAULT_VALUES[key];
      throwErrorMessage(
        "kwikui-calendar",
        this.id,
        key,
        value,
        DEFAULT_VALUES[key]
      );
    }
  }

  setDisabled() {
    if (this.formGroup.controls[this.formControlName] !== undefined) {
      if (this.disabled === true) {
        this.formGroup.controls[this.formControlName].disable({
          emitEvent: false
        });
      } else {
        this.formGroup.controls[this.formControlName].enable({
          emitEvent: false
        });
      }
    }
  }

  handleOnDayClick(day: TuiDay) {
    this.value = day;

    this.emitEvent(this.onDayClick, day);
    this.emitEvent(this.getKeyValue, {
      key: this.formControlName,
      value: this.value
    });
  }

  handleOnMonthChange(month: TuiMonth) {
    this.emitEvent(this.onMonthChange, month);
  }

  emitEvent(event: any, data: any) {
    event.emit(data);
  }

  /** Method Implementations for Abstract Control */
  writeValue(value: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
